import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

export const LABEL = 'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500';
export const SECTION_TITLE = 'font-heading text-sm font-bold uppercase tracking-wide text-slate-900';

export const Field = ({ label, value, onChange, type = 'text', testId, placeholder, readOnly }) => (
  <label className="block">
    <span className={LABEL}>{label}</span>
    <Input
      type={type}
      value={value ?? ''}
      readOnly={readOnly}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      className="h-9 text-sm"
      data-testid={testId}
    />
  </label>
);

export const ChoiceRow = ({ label, options, value, onChange, testId }) => (
  <div>
    <span className={LABEL}>{label}</span>
    <div className="flex flex-wrap gap-1.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => onChange(value === option.value ? '' : option.value)}
          className={`rounded-full border px-3 py-1 text-xs transition-colors duration-150 ${
            value === option.value
              ? 'border-blue-600 bg-blue-600 text-white'
              : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300'
          }`}
          data-testid={`${testId}-${option.value}`}
        >
          {option.label}
        </button>
      ))}
    </div>
  </div>
);

export const MultiChoiceRow = ({ label, options, values = [], onChange, testId }) => (
  <div>
    <span className={LABEL}>{label}</span>
    <div className="flex flex-wrap gap-1.5">
      {options.map((option) => {
        const active = values.includes(option);
        return (
          <button
            key={option}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(active ? values.filter((item) => item !== option) : [...values, option])}
            className={`rounded-full border px-3 py-1 text-xs transition-colors duration-150 ${
              active ? 'border-emerald-600 bg-emerald-600 text-white'
                : 'border-slate-200 bg-white text-slate-600 hover:border-emerald-300'
            }`}
            data-testid={`${testId}-${option.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          >
            {option}
          </button>
        );
      })}
    </div>
  </div>
);

const STATUS_OPTIONS = [['yes', 'Yes'], ['no', 'No'], ['na', 'N/A']];

export const ChecklistTable = ({ items, onChange, testIdPrefix }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[720px] text-sm">
      <thead>
        <tr className="border-b text-left text-[11px] uppercase tracking-wide text-slate-500">
          <th className="w-8 py-2">#</th>
          <th className="py-2">Review point</th>
          <th className="w-[190px] py-2">Status</th>
          <th className="w-[26%] py-2">Remarks / action</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, index) => (
          <tr key={item.id} className="border-b align-top last:border-0" data-testid={`${testIdPrefix}-row-${index + 1}`}>
            <td className="py-2 text-slate-400">{index + 1}</td>
            <td className="py-2 pr-3 leading-snug">{item.point}</td>
            <td className="py-2">
              <div className="flex gap-1">
                {STATUS_OPTIONS.map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={item.status === value}
                    onClick={() => onChange(index, { status: item.status === value ? null : value })}
                    className={`rounded-md border px-2.5 py-1 text-xs transition-colors duration-150 ${
                      item.status === value
                        ? value === 'no'
                          ? 'border-rose-600 bg-rose-600 text-white'
                          : value === 'yes'
                            ? 'border-emerald-600 bg-emerald-600 text-white'
                            : 'border-slate-500 bg-slate-500 text-white'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400'
                    }`}
                    data-testid={`${testIdPrefix}-${index + 1}-${value}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </td>
            <td className="py-2">
              <Input
                value={item.remarks ?? ''}
                onChange={(event) => onChange(index, { remarks: event.target.value })}
                placeholder={item.status === 'no' ? 'Required for a "No"' : 'Optional'}
                className={`h-9 text-sm ${item.status === 'no' && !item.remarks ? 'border-rose-300' : ''}`}
                data-testid={`${testIdPrefix}-${index + 1}-remarks`}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const RowsTable = ({ columns, rows, onChange, onAdd, onRemove, testIdPrefix, minWidth = 900 }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" style={{ minWidth }}>
      <thead>
        <tr className="border-b text-left text-[11px] uppercase tracking-wide text-slate-500">
          {columns.map((column) => <th key={column.key} className="py-2 pr-2" style={{ width: column.width }}>{column.label}</th>)}
          {onRemove && <th className="w-8 py-2" />}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={row.id} className="border-b last:border-0" data-testid={`${testIdPrefix}-row-${index + 1}`}>
            {columns.map((column) => (
              <td key={column.key} className="py-1.5 pr-2">
                {column.readOnly ? (
                  <span className="text-sm text-slate-700">{row[column.key]}</span>
                ) : (
                  <Input
                    type={column.type || 'text'}
                    value={row[column.key] ?? ''}
                    placeholder={column.placeholder}
                    onChange={(event) => onChange(index, { [column.key]: event.target.value })}
                    className="h-8 text-sm"
                    data-testid={`${testIdPrefix}-${index + 1}-${column.key}`}
                  />
                )}
              </td>
            ))}
            {onRemove && (
              <td className="py-1.5">
                <Button size="icon" variant="ghost" onClick={() => onRemove(index)} data-testid={`${testIdPrefix}-${index + 1}-remove`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
    {onAdd && (
      <Button size="sm" variant="outline" className="mt-2" onClick={onAdd} data-testid={`${testIdPrefix}-add`}>
        <Plus className="mr-1 h-3.5 w-3.5" /> Add row
      </Button>
    )}
  </div>
);
